// Single source of truth for A4 page geometry and rows-per-page math.
//
// WHY THIS EXISTS
// The recurring "last row cut off by the footer" bug (and its mirror image, the
// "empty band at the bottom of the page" complaint) both come from the SAME
// fragile mechanism: each section template held a hand-tuned ROWS_PER_PAGE
// magic number, and Preview.js held a SEPARATE mirror copy for page-number
// math. Every time we nudged one for one building, we either desynced the two
// (page numbers drift) or re-introduced clipping on another building.
//
// The durable fix is to STOP guessing: derive the row count from the real,
// fixed content-area height and the deterministic (lineHeight-pinned) row
// height. Both the template and Preview.js call rowsPerPage() with the same
// inputs, so they can never disagree, and the result is always the largest
// row count that provably fits WITHOUT the last row reaching the footer.
//
// A4 @ 96dpi: portrait 794×1123, landscape 1123×794 (see A4PageFrame.jsx).
// The A4PageFrame chrome (header band + footer band + content padding + the
// left-aligned section-title line) consumes a fixed amount of vertical space,
// measured from the actual A4PageFrame styles below. We subtract a small safety
// margin so sub-pixel rounding during html-to-image capture can never push the
// final row under the 1px footer border.

export const A4 = {
  portrait: { width: 794, height: 1123 },
  landscape: { width: 1123, height: 794 },
};

// Usable content height per orientation, in px — the height of the region a
// table body may occupy before its last row reaches the footer border.
//
// The A4PageFrame header and footer are now HARD-PINNED to fixed heights
// (header height:96, footer height:60 in A4PageFrame.jsx), so the flex:1 content
// area is DETERMINISTIC for every building regardless of address line count:
//   landscape: 794 − 96 − 60 − 1 (footer border) − 16 (content padding) − 14
//              (section-title line) ≈ 607 px usable row region.
//   portrait : 1123 − 96 − 60 − 1 − 16 − 14 ≈ 936 px.
// (The earlier variable-header clipping — where a 2-line address shrank the
// region and cropped the last Coûts row — no longer applies now that the chrome
// is pinned.) Client feedback pass #3: fill the empty space. We set the usable
// height a little below the real region for a one-row safety cushion against
// sub-pixel html-to-image rounding:
//   landscape 590 → floor(590/25) = 23 rows (was 21)
//   portrait  912 → floor(912/19) = 48, capped to 47 for the cushion (was 46)
//
// Row heights are additionally PINNED in each template's `td` style
// (boxSizing:border-box + explicit height) so EVERY row — invoice, subtotal,
// grand total — is exactly ROW_HEIGHT. Pinned chrome + pinned rows + a sub-real
// usable height = the clip is impossible, not merely unlikely.
const USABLE_BODY_HEIGHT = { landscape: 590, portrait: 912 };

// Deterministic per-row heights — MUST match the pinned `height`+`lineHeight`
// on each template's `td` style (see e.g. CostDetails.jsx `td`):
//   landscape tables (Surfaces / Coûts / Résultats): 25 px
//   portrait tables  (Compteurs):                    19 px
export const ROW_HEIGHT = { landscape: 25, portrait: 19 };

// Largest whole number of body rows that fits the usable content area without
// the last row touching the footer. Deterministic → the template and the
// Preview.js mirror pass the same args and get the same answer, so page-number
// math can never drift from the actual page break.
export function rowsPerPage(orientation = "landscape") {
  const rowH = ROW_HEIGHT[orientation];
  return Math.max(1, Math.floor(USABLE_BODY_HEIGHT[orientation] / rowH));
}

// Precomputed constants:
//   landscape: floor(590 / 25) = 23
//   portrait:  floor(912 / 19) = 48 → capped to 47 (one-row cushion)
export const ROWS_PER_PAGE_LANDSCAPE = rowsPerPage("landscape"); // 23
export const ROWS_PER_PAGE_PORTRAIT = Math.min(47, rowsPerPage("portrait")); // 47
